import React, { useEffect, useState } from "react";
import { staffWorkReportHistory } from "../../../api/Staff";
import { SweetAlertShow } from "../../master/Master";
import { getBatchList } from "../../../api/BatchService";

const WorkReportTab = ({ data }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [batchId, setBatchId] = useState("");
  const [batches, setBatches] = useState([]);
  const [dateError, setDateError] = useState("");

  const hasActiveFilters = Boolean(fromDate || toDate || batchId);

  const fetchReports = async (targetPage = 1) => {
    if (!data?.id) return;

    try {
      setLoading(true);
      setError("");

      let query = `staff_id=${data.id}&page=${targetPage}&per_page=10`;
      if (fromDate) query += `&from_date=${fromDate}`;
      if (toDate) query += `&to_date=${toDate}`;
      if (batchId) query += `&batch_id=${batchId}`;

      const response = await staffWorkReportHistory(query);

      if (response.status === 200 && response.data?.status) {
        setReports(response.data.data?.data || []);
        setLastPage(response.data.data?.last_page || 1);
        setTotal(response.data.data?.total ?? 0);
        setPage(targetPage);
      } else {
        setReports([]);
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      setReports([]);
      setError(err?.response?.data?.message ?? err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(1);
  }, [data?.id]);

  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const response = await getBatchList();

        if (response.status === 200 && response.data?.status) {
          setBatches(response.data.data || []);
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchBatches();
  }, []);

  const handleFilter = () => {
    if (fromDate && toDate && fromDate > toDate) {
      setDateError("'From Date' can't be after 'To Date'.");
      return;
    }
    setDateError("");
    fetchReports(1);
  };

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setBatchId("");
    setDateError("");

    fetchReports(1);
  };

  const startRecord = total === 0 ? 0 : (page - 1) * 10 + 1;
  const endRecord = Math.min(page * 10, total);

  return (
    <div>
      {/* ---------------- FILTERS ----------------
          Uses flex-wrap with min-widths rather than Bootstrap's col-md-*,
          because this panel's actual width varies by layout (e.g. sitting
          next to a sidebar) and doesn't track the viewport breakpoint that
          col-md-* assumes. Flex-wrap lets groups drop to the next line
          instead of overflowing the container. */}
      <div className="d-flex flex-wrap gap-3 mb-2">
        {/* From Date */}
        <div style={{ flex: "1 1 160px", minWidth: 160 }}>
          <label className="form-label small text-muted">From Date</label>

          <input
            type="date"
            className="form-control"
            value={fromDate}
            max={toDate || undefined}
            onChange={(e) => {
              setFromDate(e.target.value);
              setDateError("");
            }}
          />
        </div>

        {/* To Date */}
        <div style={{ flex: "1 1 160px", minWidth: 160 }}>
          <label className="form-label small text-muted">To Date</label>

          <input
            type="date"
            className="form-control"
            value={toDate}
            min={fromDate || undefined}
            onChange={(e) => {
              setToDate(e.target.value);
              setDateError("");
            }}
          />
        </div>

        {/* Batch */}
        <div style={{ flex: "1 1 180px", minWidth: 180 }}>
          <label className="form-label small text-muted">Batch</label>

          <select
            className="form-select"
            value={batchId}
            onChange={(e) => setBatchId(e.target.value)}
          >
            <option value="">All Batches</option>

            {batches.map((batch) => (
              <option key={batch.id} value={batch.id}>
                {batch.title}
              </option>
            ))}
          </select>
        </div>

        {/* Buttons */}
        <div
          className="d-flex align-items-end gap-2"
          style={{ flex: "0 0 auto" }}
        >
          <button
            type="button"
            className="btn btn-primary text-nowrap"
            onClick={handleFilter}
          >
            <i className="ri-filter-3-line me-1" aria-hidden="true"></i>
            Filter
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary text-nowrap"
            onClick={handleReset}
            disabled={!hasActiveFilters}
          >
            <i className="ri-refresh-line me-1" aria-hidden="true"></i>
            Reset
          </button>
        </div>
      </div>

      {dateError && (
        <div className="alert alert-warning py-2 px-3 small mb-3" role="alert">
          {dateError}
        </div>
      )}

      {hasActiveFilters && !dateError && (
        <div className="mb-3">
          <span className="badge bg-label-info">
            <i className="ri-filter-3-line me-1" aria-hidden="true"></i>
            Filters applied
          </span>
        </div>
      )}

      {/* ---------------- ERROR ---------------- */}
      {!loading && error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* ---------------- EMPTY STATE ---------------- */}
      {!loading && !error && reports.length === 0 && (
        <div className="text-center py-5">
          <i className="ri-file-list-3-line fs-1 text-muted" aria-hidden="true"></i>
          <p className="text-muted mt-2 mb-0">
            No work reports found{hasActiveFilters ? " for the selected filters." : "."}
          </p>
          {hasActiveFilters && (
            <button
              type="button"
              className="btn btn-link btn-sm"
              onClick={handleReset}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* ---------------- REPORT LIST ---------------- */}
      {!loading && !error && reports.length > 0 && (
        <>
          <div
            className="table-responsive position-relative"
            style={{ opacity: loading ? 0.5 : 1, transition: "opacity 0.15s" }}
          >
            <table className="table table-borderless align-middle">
              <thead>
                <tr className="border-bottom">
                  <th>Date</th>
                  <th>Batch</th>
                  <th>Subject</th>
                  <th>Period</th>
                  <th>Topic Covered</th>
                  <th>Work Description</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.id} className="border-bottom">
                    <td className="fw-semibold text-nowrap">
                      {report.report_date}
                      <div className="small text-muted">{report.day}</div>
                    </td>
                    <td>{report.batch?.title || "-"}</td>
                    <td>{report.subject?.name || "-"}</td>
                    <td className="text-nowrap">
                      {report.schedule?.title ? (
                        <span className="badge bg-label-primary">
                          {report.schedule.title}
                        </span>
                      ) : (
                        "-"
                      )}
                      <div className="small text-muted mt-1">
                        {report.schedule?.start_time} - {report.schedule?.end_time}
                      </div>
                    </td>
                    <td>{report.topic_covered || "-"}</td>
                    <td
                      className="text-truncate"
                      style={{ maxWidth: 280 }}
                      title={report.work_description}
                    >
                      {report.work_description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ---------------- PAGINATION ---------------- */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="small text-muted">
              {total > 0
                ? `Showing ${startRecord}-${endRecord} of ${total}`
                : ""}
            </span>

            {lastPage > 1 && (
              <div className="d-flex align-items-center gap-3">
                <button
                  className="btn btn-outline-primary btn-sm"
                  disabled={page <= 1 || loading}
                  onClick={() => fetchReports(page - 1)}
                  aria-label="Previous page"
                >
                  <i className="ri-arrow-left-line me-1" aria-hidden="true"></i>
                  Previous
                </button>

                <span className="small text-muted">
                  Page {page} of {lastPage}
                </span>

                <button
                  className="btn btn-outline-primary btn-sm"
                  disabled={page >= lastPage || loading}
                  onClick={() => fetchReports(page + 1)}
                  aria-label="Next page"
                >
                  Next
                  <i className="ri-arrow-right-line ms-1" aria-hidden="true"></i>
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ---------------- INITIAL LOADING (no data yet) ---------------- */}
      {loading && reports.length === 0 && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkReportTab;